import { useEffect, useState } from 'react';

import { FormattedNumberInput } from '../../../common/FormattedNumberInput/FormattedNumberInput';
import { translate } from '../../../common/helpers/translate.helper';
import { IconCornerDownRight } from '../../../common/icons/cdl/CornerDownRight';
import { Box } from '../../../common/ui/Box';
import { Button } from '../../Button/Button';
import { CoreInputProps } from '../../Input/components/CoreInput';
import { Select } from '../../Select/Select';
import { Text } from '../../Text/Text';
import { FilterPill, FilterPillProps } from '../FilterPill/FilterPill';

import { NumericOperator } from './numericOperator';

export interface FilterNumericValue {
    operator: NumericOperator;
    value: number;
}

interface OperatorOption {
    label: string;
    value: NumericOperator;
}

interface FilterNumericProps extends Pick<FilterPillProps, 'label'> {
    filterByLabel?: string;
    value?: FilterNumericValue;
    onChange?: (value?: FilterNumericValue) => void;
    inputTrailingVisual?: CoreInputProps['trailingVisual'];
}

const getOperatorOptions = (): OperatorOption[] => [
    { label: translate('global.filterNumeric.isGreaterThan'), value: NumericOperator.GREATER_THAN },
    { label: translate('global.filterNumeric.isLessThan'), value: NumericOperator.LESS_THAN },
    { label: translate('global.filterNumeric.isEqualTo'), value: NumericOperator.EQUAL_TO },
];

const formatPillValue = (value?: FilterNumericValue): string | undefined => {
    if (!value) return undefined;

    const numericValueAsString = String(value.value);

    switch (value.operator) {
        case NumericOperator.GREATER_THAN:
            return translate('global.filterNumeric.moreThan', { value: numericValueAsString });
        case NumericOperator.LESS_THAN:
            return translate('global.filterNumeric.lessThan', { value: numericValueAsString });
        case NumericOperator.EQUAL_TO:
            return translate('global.filterNumeric.equalTo', { value: numericValueAsString });
    }
};

export const FilterNumeric = (props: FilterNumericProps) => {
    const [operator, setOperator] = useState<NumericOperator>(props.value?.operator ?? NumericOperator.GREATER_THAN);
    const [numericInputValue, setNumericInputValue] = useState<number | undefined>(props.value?.value);

    useEffect(() => {
        setOperator(props.value?.operator ?? NumericOperator.GREATER_THAN);
        setNumericInputValue(props.value?.value);
    }, [props.value]);

    const operatorOptions = getOperatorOptions();
    const selectedOption = operatorOptions.find((option) => option.value === operator) ?? operatorOptions[0];
    const draftValue =
        numericInputValue !== undefined
            ? {
                  operator,
                  value: numericInputValue,
              }
            : undefined;

    return (
        <FilterPill
            label={props.label}
            value={formatPillValue(draftValue)}
            onClear={() => {
                setOperator(NumericOperator.GREATER_THAN);
                setNumericInputValue(undefined);
                props.onChange?.(undefined);
            }}
            onPopoverClose={() => {
                setOperator(props.value?.operator ?? NumericOperator.GREATER_THAN);
                setNumericInputValue(props.value?.value);
            }}
        >
            {(closePopover) => (
                <Box padding="12px" display="flex" flexDirection="column" gap={16}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={16}>
                        <Text variant="body" fontWeight={600}>
                            {props.filterByLabel}
                        </Text>
                        <Button
                            size="small"
                            emphasis="high"
                            onClick={() => {
                                if (numericInputValue !== undefined) {
                                    props.onChange?.({ operator, value: numericInputValue });
                                }
                                closePopover();
                            }}
                        >
                            {translate('global.filterNumeric.apply')}
                        </Button>
                    </Box>
                    <Box display="flex" flexDirection="column" gap={8}>
                        <Select<OperatorOption>
                            options={operatorOptions}
                            value={selectedOption}
                            onChange={(option) => {
                                if (option) {
                                    setOperator(option.value);
                                }
                            }}
                        />
                        <Box display="flex" alignItems="center" gap={8}>
                            <IconCornerDownRight width={16} height={16} />
                            <Box flex={1}>
                                <FormattedNumberInput
                                    value={numericInputValue !== undefined ? String(numericInputValue) : ''}
                                    onValueChange={(rawValue) => {
                                        if (!rawValue) {
                                            setNumericInputValue(undefined);
                                            return;
                                        }

                                        const parsedValue = Number(rawValue);
                                        setNumericInputValue(Number.isNaN(parsedValue) ? undefined : parsedValue);
                                    }}
                                    decimalsLimit={0}
                                    allowNegativeValue={false}
                                    trailingVisual={props.inputTrailingVisual}
                                />
                            </Box>
                        </Box>
                    </Box>
                </Box>
            )}
        </FilterPill>
    );
};
